// ✅ src/pages/VehicleV2/TyreMasterReport.jsx
// Overflow contained + Excel/CSV export

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/tyreMasterReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const raw = toList(arr).map((x) => {
    if (typeof x === "string") return { label: x, value: x, raw: x };

    const label = toStr(
      x.label ??
        x.name ??
        x.text ??
        x.fld_branchname ??
        x.fld_vehicletypename ??
        x.fld_tyresize ??
        x.fld_tyremodel ??
        x.fld_tyreserialno ??
        x.fld_suppliername ??
        x.value ??
        fallbackLabel,
      fallbackLabel
    );

    const value = toStr(
      x.value ??
        x.id ??
        x.key ??
        x.fld_branchid ??
        x.fld_vehicletypeindex ??
        x.fld_id ??
        x.label ??
        fallbackLabel,
      fallbackLabel
    );

    return { label, value, ...x };
  });

  const hasSelect = raw.some((x) => toStr(x.value).toLowerCase() === "select");
  if (!raw.length) return [{ label: fallbackLabel, value: fallbackLabel }];
  return hasSelect ? raw : [{ label: fallbackLabel, value: fallbackLabel }, ...raw];
};

const defaultFiltersByRole = (branchType, branchId) =>
  toStr(branchType).toUpperCase() !== "HO"
    ? {
        fromDate: "",
        toDate: "",
        branch: String(branchId),
        vehicleCode: "",
        vehicleName: "",
        regNo: "",
        tyreModel: "Select",
        tyreSize: "Select",
        tyreSerial: "Select",
        tyreMake: "",
        tyreStatus: "Select",
        supplier: "Select",
        vehicleMainType: "Select",
      }
    : {
        fromDate: "",
        toDate: "",
        branch: "Select",
        vehicleCode: "",
        vehicleName: "",
        regNo: "",
        tyreModel: "Select",
        tyreSize: "Select",
        tyreSerial: "Select",
        tyreMake: "",
        tyreStatus: "Select",
        supplier: "Select",
        vehicleMainType: "Select",
      };

const fmtDate = (v) => (v ? toStr(v) : "");

const cellVal = (r, keys) => {
  for (const k of keys) {
    if (r?.[k] !== undefined && r?.[k] !== null && String(r[k]).trim() !== "") {
      return String(r[k]);
    }
  }
  return "";
};

const csvEscape = (v) => {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export default function TyreMasterReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const didInitRef = useRef(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isBusy = pageLoading || dropdownLoading || listLoading || exporting;

  const [showFilters, setShowFilters] = useState(true);
  const [tyreType, setTyreType] = useState("new");

  const [dd, setDd] = useState({
    branches: [{ label: "Select", value: "Select" }],
    tyreSizes: [{ label: "Select", value: "Select" }],
    tyreModels: [{ label: "Select", value: "Select" }],
    tyreSerials: [{ label: "Select", value: "Select" }],
    suppliers: [{ label: "Select", value: "Select" }],
    vehicleTypes: [{ label: "Select", value: "Select" }],
    statuses: [{ label: "Select", value: "Select" }],
  });

  const baseDefaultFilters = useMemo(
    () => defaultFiltersByRole(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const [filters, setFilters] = useState(baseDefaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const pageButtons = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, 4, 5];
    if (page >= totalPages - 2) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [page - 2, page - 1, page, page + 1, page + 2];
  }, [page, totalPages]);

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () => `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const inputHeadClass = useMemo(() => `${buttonClass} text-white`, [buttonClass]);

  const buildListParams = useCallback(
    (nextFilters, nextTyreType, targetPage, targetSize) => ({
      branchId: session.branchId,
      branchType: session.branchType,
      loginUserId: session.loginUserId,
      tyreType: nextTyreType,
      fromDate: nextFilters.fromDate,
      toDate: nextFilters.toDate,
      branch:
        toStr(session.branchType).toUpperCase() === "HO"
          ? isSelect(nextFilters.branch)
            ? "Select"
            : nextFilters.branch
          : String(session.branchId),
      vehicleCode: nextFilters.vehicleCode,
      vehicleName: nextFilters.vehicleName,
      regNo: nextFilters.regNo,
      tyreModel: isSelect(nextFilters.tyreModel) ? "Select" : nextFilters.tyreModel,
      tyreSize: isSelect(nextFilters.tyreSize) ? "Select" : nextFilters.tyreSize,
      tyreSerial: isSelect(nextFilters.tyreSerial) ? "Select" : nextFilters.tyreSerial,
      tyreMake: nextFilters.tyreMake,
      tyreStatus: isSelect(nextFilters.tyreStatus) ? "Select" : nextFilters.tyreStatus,
      supplier: isSelect(nextFilters.supplier) ? "Select" : nextFilters.supplier,
      vehicleMainType: isSelect(nextFilters.vehicleMainType)
        ? "Select"
        : nextFilters.vehicleMainType,
      page: targetPage,
      pageSize: targetSize,
    }),
    [session]
  );

  const loadInit = useCallback(
    async (nextFilters = filters, nextTyreType = tyreType) => {
      try {
        setDropdownLoading(true);
        const params = {
          branchId: session.branchId,
          branchType: session.branchType,
          tyreType: nextTyreType,
          branch:
            toStr(session.branchType).toUpperCase() === "HO"
              ? nextFilters.branch
              : String(session.branchId),
          status: isSelect(nextFilters.tyreStatus) ? "Select" : nextFilters.tyreStatus,
        };
        const { data } = await axios.get(`${API_BASE}/init`, { params, headers: commonHeaders });
        if (!data?.success) throw new Error(data?.message || "Failed to load dropdowns");
        setDd({
          branches: normalizeOptionList(data.branches, "Select"),
          tyreSizes: normalizeOptionList(data.tyreSizes, "Select"),
          tyreModels: normalizeOptionList(data.tyreModels, "Select"),
          tyreSerials: normalizeOptionList(data.tyreSerials, "Select"),
          suppliers: normalizeOptionList(data.suppliers, "Select"),
          vehicleTypes: normalizeOptionList(data.vehicleTypes, "Select"),
          statuses: normalizeOptionList(data.statuses, "Select"),
        });
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");
      } finally {
        setDropdownLoading(false);
      }
    },
    [filters, tyreType, session, commonHeaders]
  );

  const fetchList = useCallback(
    async (opts = {}, nextFilters = filters, nextTyreType = tyreType) => {
      try {
        setListLoading(true);
        const targetPage = opts.page ?? page;
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(nextFilters, nextTyreType, targetPage, pageSize),
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Failed to load report");
        setRows(toList(data.rows));
        setPage(toInt(data.page, targetPage));
        setTotal(toInt(data.total, 0));
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
        setTotal(0);
      } finally {
        setListLoading(false);
      }
    },
    [filters, tyreType, page, pageSize, buildListParams, commonHeaders]
  );

  const loadPage = useCallback(
    async (nextTyreType = tyreType) => {
      try {
        setPageLoading(true);
        const reset = defaultFiltersByRole(session.branchType, session.branchId);
        setFilters(reset);
        setRows([]);
        setTotal(0);
        setPage(1);
        await loadInit(reset, nextTyreType);
      } finally {
        setPageLoading(false);
      }
    },
    [tyreType, session.branchType, session.branchId, loadInit]
  );

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBranchChange = useCallback(
    async (e) => {
      const next = {
        ...filters,
        branch: e.target.value,
        tyreSize: "Select",
        tyreModel: "Select",
        tyreSerial: "Select",
        supplier: "Select",
      };
      setFilters(next);
      await loadInit(next, tyreType);
    },
    [filters, tyreType, loadInit]
  );

  const handleStatusChange = useCallback(
    async (e) => {
      const next = { ...filters, tyreStatus: e.target.value, tyreSerial: "Select" };
      setFilters(next);
      await loadInit(next, tyreType);
    },
    [filters, tyreType, loadInit]
  );

  const handleSearch = useCallback(async () => {
    await fetchList({ page: 1 }, filters, tyreType);
  }, [fetchList, filters, tyreType]);

  const handleView = useCallback(async () => {
    const reset = defaultFiltersByRole(session.branchType, session.branchId);
    setFilters(reset);
    await loadInit(reset, tyreType);
    await fetchList({ page: 1 }, reset, tyreType);
  }, [session.branchType, session.branchId, tyreType, loadInit, fetchList]);

  const handleRefresh = useCallback(async () => {
    await loadPage(tyreType);
  }, [loadPage, tyreType]);

  const handleReset = useCallback(() => {
    setFilters(defaultFiltersByRole(session.branchType, session.branchId));
    setRows([]);
    setTotal(0);
    setPage(1);
  }, [session.branchType, session.branchId]);

  const handleTyreTypeChange = useCallback(
    async (value) => {
      if (value === tyreType) return;
      setTyreType(value);
      const reset = defaultFiltersByRole(session.branchType, session.branchId);
      setFilters(reset);
      setRows([]);
      setTotal(0);
      setPage(1);
      await loadInit(reset, value);
    },
    [tyreType, session.branchType, session.branchId, loadInit]
  );

  const handlePrintRow = useCallback(
    (row) => {
      const tyreId =
        row?.fld_tyreid ||
        row?.TyreId ||
        row?.fld_newtyre_id ||
        row?.fld_Retrededtyre_id ||
        row?.fld_tyremasid ||
        row?.id;
      if (!tyreId) {
        toast.error("Tyre ID not found in row");
        return;
      }
      navigate("/vehicle/TyreMasterReport_Print", { state: { tyreType, tyreId } });
    },
    [navigate, tyreType]
  );

  const handlePrint = useCallback(() => {
    navigate("/vehicle/TyreMasterReport_AllPrint", {
      state: { tyreType, filters, page, pageSize, total },
    });
  }, [navigate, tyreType, filters, page, pageSize, total]);

  const handleExport = useCallback(async () => {
    try {
      setExporting(true);
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: buildListParams(filters, tyreType, 1, 100000),
        headers: commonHeaders,
      });
      const list = toList(data?.rows);
      if (!list.length) {
        toast.warn("No records to export");
        return;
      }

      const headers = [
        "S.No",
        "Vehicle Main Type",
        "Vehicle Code",
        "Vehicle Name",
        "Vehicle Reg No",
        "Tyre Size",
        "Tyre Serial",
        "Tyre Model",
        "Tyre Make",
        "Issued Branch",
        "Current Branch",
        "Issue Date",
        "Supplier",
      ];

      const lines = [
        headers.map(csvEscape).join(","),
        ...list.map((r, i) =>
          [
            i + 1,
            cellVal(r, ["VehicleType", "fld_vehicletypename", "VehicleMainType"]),
            cellVal(r, ["vehicleCode", "fld_vehicle_code", "VehicleCode"]),
            cellVal(r, ["vehicleName", "fld_vehicle_name", "VehicleName"]),
            cellVal(r, ["regNo", "fld_vehicle_regno", "VehicleRegNo"]),
            cellVal(r, ["tyreSize", "fld_tyresize", "TyreSize"]),
            cellVal(r, ["tyreSerial", "fld_tyreserialno", "TyreSerial"]),
            cellVal(r, ["tyreModel", "fld_tyremodel", "TyreModel"]),
            cellVal(r, ["tyreMake", "fld_tyremake", "TyreMake"]),
            cellVal(r, ["issuedBranch", "fld_issuebranch", "IssuedBranch"]),
            cellVal(r, ["currentBranch", "fld_branchname", "CurrentBranch"]),
            fmtDate(cellVal(r, ["issueDate", "fld_issueddate", "IssueDate"])),
            cellVal(r, ["supplier", "fld_purchasefrom", "Supplier"]),
          ]
            .map(csvEscape)
            .join(",")
        ),
      ];

      const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
        type: "application/vnd.ms-excel;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TyreMasterReport_${tyreType}_${new Date().toISOString().slice(0, 10)}.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${list.length} record(s)`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }, [buildListParams, filters, tyreType, commonHeaders]);

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const goPage = useCallback(
    async (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      await fetchList({ page: next }, filters, tyreType);
    },
    [fetchList, filters, tyreType, totalPages]
  );

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage(tyreType);
  }, [loadPage, tyreType]);

  const tdClass = `px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-4 md:p-6 max-w-full overflow-x-hidden ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TYRE MASTER REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-4 md:p-6 mt-6 rounded-2xl shadow-xl border max-w-full overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tyre Master Report
              </h1>
              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} • User ID: {session.loginUserId || "-"} • Records: {total}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setShowFilters((p) => !p)} type="button" className="px-4 py-2 border rounded-lg">
                <FaFilter className="inline mr-2" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              <button
                onClick={handleExport}
                disabled={isBusy}
                type="button"
                className="px-4 py-2 border rounded-lg"
              >
                <FaFileExport className="inline mr-2" />
                {exporting ? "Exporting..." : "Export Excel"}
              </button>
              <button onClick={handleRefresh} type="button" className="px-4 py-2 border rounded-lg">
                <FaSyncAlt className="inline mr-2" />
                Refresh
              </button>
              <button onClick={handleBack} type="button" className="px-4 py-2 border rounded-lg">
                <FaArrowLeft className="inline mr-2" />
                Back
              </button>
            </div>
          </div>

          {pageLoading || dropdownLoading ? (
            <div className="text-center py-16">Loading...</div>
          ) : (
            <>
              <div className="flex gap-6 mb-6">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={tyreType === "new"}
                    onChange={() => handleTyreTypeChange("new")}
                    disabled={isBusy}
                  />
                  New Tyre
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={tyreType === "retread"}
                    onChange={() => handleTyreTypeChange("retread")}
                    disabled={isBusy}
                  />
                  Retread Tyre
                </label>
              </div>

              {showFilters && (
                <div className={`rounded-xl p-4 md:p-6 mb-6 border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className={labelClass}>From Date</label>
                      <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>To Date</label>
                      <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="branch"
                        value={filters.branch}
                        onChange={handleBranchChange}
                        className={inputClass}
                        disabled={toStr(session.branchType).toUpperCase() !== "HO"}
                      >
                        {dd.branches.map((b, idx) => (
                          <option key={`${b.value}-${idx}`} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Vehicle Name</label>
                      <input name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Vehicle Reg No</label>
                      <input name="regNo" value={filters.regNo} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Tyre Model</label>
                      <select name="tyreModel" value={filters.tyreModel} onChange={handleChange} className={inputClass}>
                        {dd.tyreModels.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Tyre Size</label>
                      <select name="tyreSize" value={filters.tyreSize} onChange={handleChange} className={inputClass}>
                        {dd.tyreSizes.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Tyre Serial No</label>
                      <select name="tyreSerial" value={filters.tyreSerial} onChange={handleChange} className={inputClass}>
                        {dd.tyreSerials.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Tyre Make</label>
                      <input name="tyreMake" value={filters.tyreMake} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Tyre Status</label>
                      <select name="tyreStatus" value={filters.tyreStatus} onChange={handleStatusChange} className={inputClass}>
                        {dd.statuses.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Supplier</label>
                      <select name="supplier" value={filters.supplier} onChange={handleChange} className={inputClass}>
                        {dd.suppliers.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Vehicle Main Type</label>
                      <select name="vehicleMainType" value={filters.vehicleMainType} onChange={handleChange} className={inputClass}>
                        {dd.vehicleTypes.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-end">
                    <button onClick={handleReset} type="button" className="px-4 py-2 border rounded-lg">Reset</button>
                    <button onClick={handleView} type="button" className="flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-lg">
                      <FaEye /> View All
                    </button>
                    <button onClick={handleSearch} type="button" className={`flex items-center gap-2 ${buttonClass} text-white px-5 py-2 rounded-lg`}>
                      <FaSearch /> Search
                    </button>
                    <button onClick={handlePrint} type="button" className={`flex items-center gap-2 ${buttonClass} text-white px-5 py-2 rounded-lg`}>
                      <FaPrint /> Print
                    </button>
                  </div>
                </div>
              )}

              <div className={`rounded-xl border max-w-full ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <FaInfoCircle className="inline w-8 h-8 text-gray-400 mb-3" />
                    <div>No Tyre Records Found</div>
                  </div>
                ) : (
                  <div className="w-full max-w-full overflow-x-auto">
                    <table className="min-w-[960px] w-full">
                      <thead className={inputHeadClass}>
                        <tr>
                          {[
                            "S.No",
                            "Main Type",
                            "Code",
                            "Name",
                            "Reg No",
                            "Model",
                            "Make",
                            "Issued Br",
                            "Current Br",
                            "Issue Date",
                            "Supplier",
                            "Print",
                          ].map((h) => (
                            <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {rows.map((r, idx) => (
                          <tr key={r.fld_tyreid ?? r.TyreId ?? r.id ?? idx} className={isDark ? "bg-gray-800" : "bg-white"}>
                            <td className={tdClass}>{(page - 1) * pageSize + idx + 1}</td>
                            <td className={tdClass}>{cellVal(r, ["VehicleType", "fld_vehicletypename", "VehicleMainType"])}</td>
                            <td className={tdClass}>{cellVal(r, ["vehicleCode", "fld_vehicle_code", "VehicleCode"])}</td>
                            <td className={tdClass}>{cellVal(r, ["vehicleName", "fld_vehicle_name", "VehicleName"])}</td>
                            <td className={tdClass}>{cellVal(r, ["regNo", "fld_vehicle_regno", "VehicleRegNo"])}</td>
                            <td className={tdClass}>{cellVal(r, ["tyreModel", "fld_tyremodel", "TyreModel"])}</td>
                            <td className={tdClass}>{cellVal(r, ["tyreMake", "fld_tyremake", "TyreMake"])}</td>
                            <td className={tdClass}>{cellVal(r, ["issuedBranch", "fld_issuebranch", "IssuedBranch"])}</td>
                            <td className={tdClass}>{cellVal(r, ["currentBranch", "fld_branchname", "CurrentBranch"])}</td>
                            <td className={tdClass}>{fmtDate(cellVal(r, ["issueDate", "fld_issueddate", "IssueDate"]))}</td>
                            <td className={tdClass}>{cellVal(r, ["supplier", "fld_purchasefrom", "Supplier"])}</td>
                            <td className="px-3 py-3 text-center sticky right-0 bg-inherit">
                              <button
                                type="button"
                                onClick={() => handlePrintRow(r)}
                                className={`inline-flex items-center px-2 py-1 rounded text-white ${buttonClass}`}
                              >
                                <FaPrint />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {total > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Page {page} of {totalPages}
                  </div>
                  <nav className="flex gap-1 flex-wrap">
                    <button onClick={() => goPage(page - 1)} disabled={page === 1} type="button" className="px-3 py-1 border rounded text-sm">
                      Previous
                    </button>
                    {pageButtons.map((p) => (
                      <button
                        key={p}
                        onClick={() => goPage(p)}
                        type="button"
                        className={`px-3 py-1 border rounded text-sm ${page === p ? `${buttonClass} text-white` : ""}`}
                      >
                        {p}
                      </button>
                    ))}
                    <button onClick={() => goPage(page + 1)} disabled={page === totalPages} type="button" className="px-3 py-1 border rounded text-sm">
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}